import Link from "next/link";
import { GenericProductCard } from "@components/productCard";
import { PrimaryButton } from "@sourceful/shared-components";
import { Image, SanityPresetCardInstance } from "@sourceful/shared-types";
import toRem from "@styles/postprocess/conversion/to-rem";
import { getSanityImgUrl } from "@lib/sanity";
import { formatPresetGroqQuery } from "@sourceful/shared-utils/sanity-utils";

const getImageUrl = (
  preset: SanityPresetCardInstance["presetCard"]["preset"],
  image: Image | null,
  useDefaultPresetImage: boolean
) => {
  if (useDefaultPresetImage && preset && preset.images) {
    return preset?.images[0]?.asset?._ref || image?.asset?._ref || "";
  }

  if (image?.asset?._ref) {
    return image?.asset?._ref || "";
  }

  if (preset?.images) {
    return preset?.images[0]?.asset?._ref || "";
  }

  return "";
};

export interface PresetCardProps {
  card: SanityPresetCardInstance;
  className?: string;
}

export const PresetCard = ({ card, className }: PresetCardProps) => {
  const {
    image,
    title,
    subtitle,
    ctaText,
    customCTA,
    useDefaultPresetImage,
    usePresetNameAsTitle,
    preset: rawPreset,
  } = card.presetCard;

  if (!rawPreset) return null;

  const sourcefulPreset = formatPresetGroqQuery(rawPreset);

  const imageUrl = getSanityImgUrl(
    getImageUrl(rawPreset, image || null, useDefaultPresetImage),
    base => base.width(300).height(300)
  );

  return (
    <GenericProductCard
      className={className}
      imageUrl={imageUrl}
      title={usePresetNameAsTitle ? rawPreset?.name || title : title}
      subtitle={subtitle}
      renderCTA={() => {
        return (
          <Link
            passHref
            href={
              customCTA
                ? customCTA.url
                : `/buy/${sourcefulPreset.productSlug}/${sourcefulPreset.slug}`
            }
            legacyBehavior
          >
            <PrimaryButton
              as="a"
              css={{
                width: "100%",
                marginTop: toRem(16),
              }}
            >
              {customCTA ? customCTA.title : ctaText}
            </PrimaryButton>
          </Link>
        );
      }}
    />
  );
};

export default PresetCard;
